<template>
  <div v-if="visible" class="dialog-overlay" @click="handleOverlayClick">
    <div class="dialog-container" @click.stop>
      <div v-if="icon" class="dialog-header">
        <div class="dialog-icon" :class="iconClass">
          <svg v-if="icon === 'warning'" class="icon-warning" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <svg v-if="icon === 'success'" class="icon-success" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 12L11 14L15 10M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <svg v-if="icon === 'error'" class="icon-error" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15 9L9 15M9 9L15 15M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <svg v-if="icon === 'info'" class="icon-info" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M13 16H12V12H11M12 8H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
      </div>
      <div class="dialog-body">
        <h3 v-if="title" class="dialog-title">{{ title }}</h3>
        <p v-if="message" class="dialog-message">{{ message }}</p>
      </div>
      <div class="dialog-footer">
        <button v-if="showCancel" class="dialog-btn cancel" @click="handleCancel">
          {{ cancelText }}
        </button>
        <button class="dialog-btn confirm" :class="confirmClass" @click="handleConfirm">
          {{ confirmText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Dialog',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: ''
    },
    message: {
      type: String,
      default: ''
    },
    icon: {
      type: String,
      default: 'warning',
      validator: (val) => ['warning', 'success', 'error', 'info'].includes(val)
    },
    showCancel: {
      type: Boolean,
      default: true
    },
    cancelText: {
      type: String,
      default: '取消'
    },
    confirmText: {
      type: String,
      default: '确定'
    },
    confirmType: {
      type: String,
      default: 'primary',
      validator: (val) => ['primary', 'danger', 'success'].includes(val)
    }
  },
  computed: {
    iconClass() {
      return {
        'icon-warning': this.icon === 'warning',
        'icon-success': this.icon === 'success',
        'icon-error': this.icon === 'error',
        'icon-info': this.icon === 'info'
      }
    },
    confirmClass() {
      return {
        'danger': this.confirmType === 'danger',
        'success': this.confirmType === 'success'
      }
    }
  },
  methods: {
    handleOverlayClick() {
      this.$emit('cancel')
    },
    handleCancel() {
      this.$emit('cancel')
    },
    handleConfirm() {
      this.$emit('confirm')
    }
  }
}
</script>

<style scoped>
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.dialog-container {
  background: white;
  border-radius: 24px;
  width: 100%;
  max-width: 340px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  overflow: hidden;
  animation: slideUp 0.4s ease;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.dialog-header {
  padding-top: 32px;
  display: flex;
  justify-content: center;
}

.dialog-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dialog-icon.icon-warning {
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
}

.dialog-icon.icon-success {
  background: linear-gradient(135deg, #34d399 0%, #10b981 100%);
}

.dialog-icon.icon-error {
  background: linear-gradient(135deg, #f87171 0%, #ef4444 100%);
}

.dialog-icon.icon-info {
  background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 100%);
}

.dialog-icon svg {
  width: 32px;
  height: 32px;
  color: white;
}

.dialog-body {
  padding: 24px 24px 20px;
  text-align: center;
}

.dialog-title {
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 8px;
}

.dialog-message {
  font-size: 15px;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
}

.dialog-footer {
  display: flex;
  border-top: 1px solid #e2e8f0;
}

.dialog-btn {
  flex: 1;
  padding: 16px;
  border: none;
  background: transparent;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.dialog-btn.cancel {
  color: #64748b;
  border-right: 1px solid #e2e8f0;
}

.dialog-btn.cancel:active {
  background: #f1f5f9;
}

.dialog-btn.confirm {
  color: #3b82f6;
}

.dialog-btn.confirm:active {
  background: #eff6ff;
}

.dialog-btn.confirm.danger {
  color: #ef4444;
}

.dialog-btn.confirm.danger:active {
  background: #fef2f2;
}

.dialog-btn.confirm.success {
  color: #10b981;
}

.dialog-btn.confirm.success:active {
  background: #ecfdf5;
}
</style>
